import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Spinner } from "@/components/shared";
import { AskTemplatesDialog } from "@/components/AskTemplatesDialog";
import { AskReportDialog } from "@/components/AskReportDialog";
import { aaLabel, AA_CATEGORIES, AA_ADMIN_QUEUES, AA_SLA_STYLE } from "@/lib/askAnything";
import { Inbox, Clock, ChevronRight, ShieldAlert, FileText, BarChart3 } from "lucide-react";

const fmt = (iso) => { try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return ""; } };

function Metric({ label, value, tint, testid }) {
  return (
    <div className="kid-card p-4" data-testid={testid}>
      <div className="text-2xl font-extrabold font-display" style={{ color: tint }}>{value}</div>
      <div className="text-xs text-kid-muted mt-0.5">{label}</div>
    </div>
  );
}

export default function AdminAskAnything() {
  const [queue, setQueue] = useState("open");
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tplOpen, setTplOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  const load = async (q = queue) => {
    setLoading(true);
    const { data: d } = await api.get("/admin/ask-anything/threads", { params: { queue: q } });
    setData(d); setLoading(false);
  };
  useEffect(() => { api.get("/admin/ask-anything/stats").then((r) => setStats(r.data)); }, []);
  useEffect(() => { load(queue); }, [queue]);

  return (
    <div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <PageHeader eyebrow="Support" title="Ask Anything" subtitle="Paid-family support inbox. Reply within 72 hours." />
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={() => setReportOpen(true)} className="kid-btn kid-btn-ghost h-10 px-4" data-testid="aa-open-report"><BarChart3 size={16} /> Report</button>
          <button onClick={() => setTplOpen(true)} className="kid-btn kid-btn-ghost h-10 px-4" data-testid="aa-manage-templates"><FileText size={16} /> Templates</button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6" data-testid="aa-admin-stats">
          <Metric label="Open" value={stats.open} tint="var(--teal)" testid="aa-stat-open" />
          <Metric label="New" value={stats.new} tint="var(--grape)" testid="aa-stat-new" />
          <Metric label="Due soon" value={stats.due_soon} tint="#B07A11" testid="aa-stat-duesoon" />
          <Metric label="Overdue" value={stats.overdue} tint="var(--coral)" testid="aa-stat-overdue" />
          <Metric label="Safety flagged" value={stats.safety_flagged} tint="var(--coral)" testid="aa-stat-safety" />
        </div>
      )}

      <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-1" data-testid="aa-admin-queues">
        {AA_ADMIN_QUEUES.map((q) => (
          <button key={q.value} onClick={() => setQueue(q.value)} data-testid={`aa-queue-${q.value}`}
            className={`text-sm font-poppins font-semibold px-3.5 py-1.5 rounded-full whitespace-nowrap transition-colors ${queue === q.value ? "bg-kid-teal text-white" : "bg-white border border-kid-line text-kid-muted hover:text-kid-teal"}`}>
            {q.label}{data?.counts?.[q.value] ? ` (${data.counts[q.value]})` : ""}
          </button>
        ))}
      </div>

      {loading ? <Spinner /> : !data.threads.length ? (
        <div className="kid-card p-10 text-center text-kid-muted" data-testid="aa-admin-empty"><Inbox size={26} className="mx-auto mb-2 opacity-60" /> No tickets in this queue.</div>
      ) : (
        <div className="space-y-3" data-testid="aa-admin-list">
          {data.threads.map((t) => {
            const sla = AA_SLA_STYLE[t.sla_status] || AA_SLA_STYLE.on_track;
            return (
              <Link key={t.id} to={`/ops/ask-anything/${t.id}`} className="kid-card p-4 flex items-center gap-4 hover:border-kid-teal transition-colors" data-testid="aa-admin-ticket">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-kid-navy truncate">{t.title}</p>
                    {t.safety_flag && <span className="inline-flex items-center gap-1 text-[.65rem] font-bold uppercase bg-kid-coral/15 text-kid-coral px-2 py-0.5 rounded-full"><ShieldAlert size={11} /> Safety</span>}
                    <span className={`text-[.65rem] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${sla.cls}`}>{sla.label}</span>
                  </div>
                  <p className="text-sm text-kid-muted truncate mt-0.5">{t.user_email} · {aaLabel(AA_CATEGORIES, t.category)} · {t.last_message_preview}</p>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xs text-kid-muted flex items-center gap-1"><Clock size={12} /> {fmt(t.sla_due_at)}</div>
                  <ChevronRight size={16} className="text-kid-line ml-auto mt-1" />
                </div>
              </Link>
            );
          })}
        </div>
      )}
      <AskTemplatesDialog open={tplOpen} onClose={() => setTplOpen(false)} />
      <AskReportDialog open={reportOpen} onClose={() => setReportOpen(false)} />
    </div>
  );
}
